import { useState, useEffect, useRef, useMemo } from "react";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  doc,
  writeBatch,
} from "firebase/firestore";
import AdminNav from "../pages/Adminnav";

const display = { fontFamily: "'Fraunces', serif" };

// ===== CUSTOM ALERT COMPONENT =====
const CustomAlert = ({ isOpen, onClose, title, message, type = "success" }) => {
  if (!isOpen) return null;

  const colors = {
    success: {
      bg: "bg-green-50",
      border: "border-green-200",
      icon: "text-green-500",
      button: "bg-green-600 hover:bg-green-700",
    },
    error: {
      bg: "bg-red-50",
      border: "border-red-200",
      icon: "text-red-500",
      button: "bg-red-600 hover:bg-red-700",
    },
  };

  const current = colors[type] || colors.success;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/40 backdrop-blur-sm animate-[fadeIn_0.3s_ease-out]">
      <div className={`${current.bg} rounded-2xl max-w-md w-full p-6 shadow-2xl transform animate-[scaleIn_0.3s_ease-out] border ${current.border}`}>
        <div className="flex flex-col items-center text-center">
          <div className={`w-16 h-16 rounded-full ${current.bg} border ${current.border} flex items-center justify-center mb-4`}>
            {type === "success" ? (
              <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            )}
          </div>
          <h3 className="text-xl font-bold text-[#3E5645]">{title}</h3>
          <p className="text-[#6B7A6E] text-sm mt-2">{message}</p>
          <button
            onClick={onClose}
            className={`mt-6 w-full py-3 ${current.button} text-white font-semibold rounded-xl transition-colors shadow-lg hover:shadow-xl`}
          >
            OK, Got it! ✨
          </button>
        </div>
      </div>
    </div>
  );
};

// ===== IMAGE SLIDER COMPONENT =====
const ProductImageSlider = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const timerRef = useRef(null);

  const validImages = images.filter(img => img && img.trim() !== '');

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (validImages.length > 1 && !isHovering) {
      timerRef.current = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % validImages.length);
      }, 3000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isHovering, validImages.length]);

  if (validImages.length <= 1) {
    return (
      <div className="slider-container">
        {validImages.length === 1 ? (
          <img src={getGoogleDriveImageUrl(validImages[0])} alt="Product" className="slider-image" />
        ) : (
          <div className="slider-placeholder">—</div>
        )}
      </div>
    );
  }

  const goToSlide = (index) => setCurrentIndex(index);
  const goToPrev = (e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev - 1 + validImages.length) % validImages.length); };
  const goToNext = (e) => { e.stopPropagation(); setCurrentIndex((prev) => (prev + 1) % validImages.length); };

  return (
    <div className="slider-container" onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
      <img src={getGoogleDriveImageUrl(validImages[currentIndex])} alt="Product" className="slider-image" />
      {validImages.length > 1 && (
        <>
          <button className="slider-arrow slider-arrow-left" onClick={goToPrev}>‹</button>
          <button className="slider-arrow slider-arrow-right" onClick={goToNext}>›</button>
          <div className="slider-dots">
            {validImages.map((_, idx) => (
              <span key={idx} className={`slider-dot ${idx === currentIndex ? 'active' : ''}`} onClick={() => goToSlide(idx)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const extractGoogleDriveId = (url) => {
  if (!url) return null;
  const match = url.match(/\/d\/([^/]+)/);
  return match ? match[1] : null;
};

const getGoogleDriveImageUrl = (url) => {
  if (!url) return "";
  if (!url.includes("drive.google.com")) return url;
  const fileId = extractGoogleDriveId(url);
  if (!fileId) return url;
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
};

// Sentinel value used by the category <select> to represent "the user
// picked the Add New Category option" — never saved to Firestore.
const NEW_CATEGORY_VALUE = "__new__";

function AdminDashboard() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [originalPrice, setOriginalPrice] = useState("");
  const [sellingPrice, setSellingPrice] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [imageUrl2, setImageUrl2] = useState("");
  const [imageUrl3, setImageUrl3] = useState("");
  const [imageUrl4, setImageUrl4] = useState("");
  const [badge, setBadge] = useState("");

  // ===== CATEGORY: fully dynamic =====
  const [category, setCategory] = useState(""); // holds the chosen existing category
  const [isNewCategory, setIsNewCategory] = useState(false); // true when "+ Add New Category" is selected
  const [newCategoryText, setNewCategoryText] = useState(""); // text typed for the new category

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [isDraggingOccurred, setIsDraggingOccurred] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // ===== ALERT STATE =====
  const [alert, setAlert] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "success",
  });

  const dragItemIndex = useRef(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  // ===== DYNAMIC CATEGORY LIST: built from whatever categories your
  // products already use. Add a brand-new one from the form below and,
  // as soon as a product is saved with it, it shows up here automatically. =====
  const existingCategories = useMemo(
    () => Array.from(new Set(products.map((p) => p.category).filter(Boolean))).sort(),
    [products]
  );

  // Pick a sensible default category once products have loaded, but only
  // if the admin hasn't already chosen something (adding first product,
  // or right after resetForm).
  useEffect(() => {
    if (!editingId && !isNewCategory && !category && existingCategories.length > 0) {
      setCategory(existingCategories[0]);
    }
    // If there are truly no categories yet anywhere (brand-new store),
    // drop straight into "type a new category" mode.
    if (!editingId && !isNewCategory && !category && existingCategories.length === 0) {
      setIsNewCategory(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingCategories]);

  // ===== SHOW ALERT FUNCTION =====
  const showAlert = (title, message, type = "success") => {
    setAlert({
      isOpen: true,
      title: title,
      message: message,
      type: type,
    });
  };

  // ===== CLOSE ALERT FUNCTION =====
  const closeAlert = () => {
    setAlert({ ...alert, isOpen: false });
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(db, "products"));
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const orderA = a.order ?? Infinity;
        const orderB = b.order ?? Infinity;
        if (orderA !== orderB) return orderA - orderB;
        const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
        const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
        return timeA - timeB;
      });
      setProducts(list);
      setIsDraggingOccurred(false);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
    setLoading(false);
  };

  useEffect(() => { fetchProducts(); }, []);

  const resetForm = () => {
    setName(""); setDescription(""); setOriginalPrice(""); setSellingPrice("");
    setImageUrl(""); setImageUrl2(""); setImageUrl3(""); setImageUrl4("");
    setBadge("");
    setCategory(existingCategories[0] || "");
    setIsNewCategory(existingCategories.length === 0);
    setNewCategoryText("");
    setEditingId(null);
  };

  const handleEditClick = (product) => {
    setEditingId(product.id);
    setName(product.name || "");
    setDescription(product.description || "");
    setOriginalPrice(product.originalPrice?.toString() || "");
    setSellingPrice(product.sellingPrice?.toString() || "");
    setImageUrl(product.imageUrl || "");
    setImageUrl2(product.imageUrl2 || "");
    setImageUrl3(product.imageUrl3 || "");
    setImageUrl4(product.imageUrl4 || "");
    setBadge(product.badge || "");
    setCategory(product.category || "");
    setIsNewCategory(false);
    setNewCategoryText("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ===== CATEGORY SELECT HANDLER =====
  const handleCategorySelectChange = (e) => {
    const value = e.target.value;
    if (value === NEW_CATEGORY_VALUE) {
      setIsNewCategory(true);
      setNewCategoryText("");
    } else {
      setIsNewCategory(false);
      setCategory(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Resolve the final category name (existing pick, or freshly typed one)
    const finalCategory = isNewCategory ? newCategoryText.trim() : category;

    if (!finalCategory) {
      showAlert("❌ Error!", "Please select or type a category.", "error");
      return;
    }

    setSaving(true);

    const finalImageUrl = getGoogleDriveImageUrl(imageUrl);
    const finalImageUrl2 = getGoogleDriveImageUrl(imageUrl2);
    const finalImageUrl3 = getGoogleDriveImageUrl(imageUrl3);
    const finalImageUrl4 = getGoogleDriveImageUrl(imageUrl4);
    const mrpPrice = Number(originalPrice);
    const salePrice = Number(sellingPrice);

    const images = [finalImageUrl, finalImageUrl2, finalImageUrl3, finalImageUrl4].filter(url => url && url.trim() !== '');

    const productData = {
      name,
      description,
      originalPrice: mrpPrice,
      sellingPrice: salePrice,
      imageUrl: finalImageUrl,
      imageUrl2: finalImageUrl2 || null,
      imageUrl3: finalImageUrl3 || null,
      imageUrl4: finalImageUrl4 || null,
      images: images,
      badge: badge || null,
      category: finalCategory,
    };

    try {
      if (editingId) {
        // ===== UPDATE PRODUCT =====
        await updateDoc(doc(db, "products", editingId), productData);

        // ===== SHOW SUCCESS ALERT =====
        showAlert(
          "✅ Product Updated!",
          `"${name}" has been successfully updated in the store.`,
          "success"
        );

        // ===== RESET FORM =====
        resetForm();
        await fetchProducts();
      } else {
        // ===== ADD NEW PRODUCT =====
        const maxOrder = products.reduce((max, p) => (typeof p.order === "number" && p.order > max ? p.order : max), -1);
        await addDoc(collection(db, "products"), { ...productData, order: maxOrder + 1, createdAt: new Date() });

        // ===== SHOW SUCCESS ALERT =====
        showAlert(
          "✅ Product Added!",
          `"${name}" has been successfully added to the store.`,
          "success"
        );

        resetForm();
        await fetchProducts();
      }
    } catch (error) {
      console.error("Error saving product:", error);
      showAlert(
        "❌ Error!",
        "Something went wrong. Please try again.",
        "error"
      );
    }
    setSaving(false);
  };

  const openDeleteModal = (product) => { setProductToDelete(product); setShowDeleteModal(true); };

  const confirmDelete = async () => {
    if (!productToDelete) return;
    setDeletingId(productToDelete.id);
    setShowDeleteModal(false);

    try {
      await deleteDoc(doc(db, "products", productToDelete.id));
      if (editingId === productToDelete.id) resetForm();
      await fetchProducts();

      showAlert(
        "🗑️ Product Deleted!",
        `"${productToDelete.name}" has been removed from the store.`,
        "success"
      );
    } catch (error) {
      console.error("Error deleting product:", error);
      showAlert(
        "❌ Error!",
        "Failed to delete product. Please try again.",
        "error"
      );
    }
    setDeletingId(null);
    setProductToDelete(null);
  };

  const cancelDelete = () => { setShowDeleteModal(false); setProductToDelete(null); };

  // ===== DRAG LOGIC =====
  const handleDragStart = (index) => { dragItemIndex.current = index; };
  const handleDragEnter = (index) => { if (dragItemIndex.current === null || dragItemIndex.current === index) return; setDragOverIndex(index); };
  const handleDragOver = (e) => { e.preventDefault(); };

  const handleDragEnd = () => {
    const fromIndex = dragItemIndex.current;
    const toIndex = dragOverIndex;
    dragItemIndex.current = null;
    setDragOverIndex(null);

    if (fromIndex === null || toIndex === null || fromIndex === toIndex) return;

    const reordered = [...products];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setProducts(reordered);
    setIsDraggingOccurred(true);
  };

  // ===== SAVE ORDER =====
  const handleSaveOrder = async () => {
    if (!isDraggingOccurred) return;

    setSaving(true);
    const withNewOrder = products.map((p, i) => ({ ...p, order: i }));

    try {
      const batch = writeBatch(db);
      withNewOrder.forEach((p) => {
        batch.update(doc(db, "products", p.id), { order: p.order });
      });
      await batch.commit();

      showAlert(
        "✅ Order Saved!",
        "Product order has been updated successfully. Changes will reflect on the Store page.",
        "success"
      );

      setIsDraggingOccurred(false);
    } catch (error) {
      console.error("Error saving order:", error);
      showAlert(
        "❌ Error!",
        "Failed to save order. Please try again.",
        "error"
      );
      await fetchProducts();
    }
    setSaving(false);
  };

  const getProductImages = (product) => [product.imageUrl, product.imageUrl2, product.imageUrl3, product.imageUrl4].filter(url => url && url.trim() !== '');

  return (
    <div className="min-h-screen w-full relative" style={{ background: "#FDFBF7" }}>
      {/* ===== CUSTOM ALERT ===== */}
      <CustomAlert
        isOpen={alert.isOpen}
        onClose={closeAlert}
        title={alert.title}
        message={alert.message}
        type={alert.type}
      />

      <style>{`
        .admin-field::placeholder { color: #A89B8C; }
        .inventory-container { max-height: calc(100vh - 200px); overflow-y: auto; padding-right: 4px; }
        .inventory-container::-webkit-scrollbar { width: 4px; }
        .inventory-container::-webkit-scrollbar-track { background: #F5F0EB; border-radius: 4px; }
        .inventory-container::-webkit-scrollbar-thumb { background: #C4B8AC; border-radius: 4px; }
        .product-card { display: grid; grid-template-columns: auto 1fr auto; align-items: center; gap: 12px; padding: 12px 16px; background: #FFFFFF; border: 1px solid #E8E0D8; border-radius: 12px; transition: all 0.2s ease; min-height: 72px; }
        .product-card:hover { box-shadow: 0 2px 12px rgba(62, 86, 69, 0.08); }
        .product-card .drag-section { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .product-card .drag-section .drag-handle { font-size: 18px; color: #C4B8AC; cursor: grab; user-select: none; padding: 4px; }
        .product-card .drag-section .index-number { font-size: 11px; color: #A89B8C; font-family: monospace; min-width: 24px; text-align: center; }
        .product-card .info-section { display: flex; align-items: center; gap: 12px; min-width: 0; flex: 1; }
        .slider-container { width: 52px; height: 52px; border-radius: 8px; background: #FBF9F2; flex-shrink: 0; overflow: hidden; position: relative; }
        .slider-image { width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s ease; }
        .slider-placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; color: #C4B8AC; font-size: 20px; }
        .slider-arrow { position: absolute; top: 50%; transform: translateY(-50%); background: rgba(0, 0, 0, 0.5); color: white; border: none; width: 18px; height: 18px; border-radius: 50%; font-size: 12px; cursor: pointer; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.3s ease; z-index: 5; }
        .slider-container:hover .slider-arrow { opacity: 1; }
        .slider-arrow:hover { background: rgba(0, 0, 0, 0.8); }
        .slider-dots { position: absolute; bottom: 2px; left: 50%; transform: translateX(-50%); display: flex; gap: 3px; z-index: 5; }
        .slider-dot { width: 5px; height: 5px; border-radius: 50%; background: rgba(255, 255, 255, 0.5); cursor: pointer; transition: all 0.3s ease; }
        .slider-dot.active { background: white; transform: scale(1.2); }
        .product-card .info-section .product-details { min-width: 0; flex: 1; }
        .product-card .info-section .product-details .product-name { font-size: 14px; font-weight: 600; color: #3E5645; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .product-card .info-section .product-details .product-desc { font-size: 12px; color: #6B7A6E; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .product-card .info-section .product-details .product-cat { font-size: 11px; font-weight: 600; color: #2F6FED; }
        .product-card .info-section .product-details .product-badge { font-size: 10px; color: #C9A24B; font-weight: 600; }
        .product-card .actions-section { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
        .product-card .actions-section .price-info { display: flex; flex-direction: column; align-items: flex-end; gap: 1px; }
        .product-card .actions-section .price-info .original-price { font-size: 10px; color: #A89B8C; text-decoration: line-through; font-family: monospace; }
        .product-card .actions-section .price-info .selling-price { font-size: 14px; font-weight: 700; color: #3E5645; font-family: monospace; }
        .product-card .actions-section .action-buttons { display: flex; gap: 6px; }
        .product-card .actions-section .action-buttons button { padding: 4px 14px; border: 1px solid #E8E0D8; border-radius: 8px; font-size: 11px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; background: transparent; color: #6B7A6E; font-family: inherit; }
        .product-card .actions-section .action-buttons .edit-btn:hover { background: rgba(62, 86, 69, 0.08); color: #3E5645; }
        .product-card .actions-section .action-buttons .del-btn:hover { background: rgba(176, 80, 58, 0.12); color: #C0392B; }
        .multi-image-badge { position: absolute; top: 2px; right: 2px; background: rgba(0, 0, 0, 0.6); color: white; font-size: 8px; padding: 1px 5px; border-radius: 8px; z-index: 4; font-weight: 600; }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-\\[fadeIn_0\\.3s_ease-out\\] {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-\\[scaleIn_0\\.3s_ease-out\\] {
          animation: scaleIn 0.3s ease-out;
        }
      `}</style>

      {/* ===== DELETE MODAL ===== */}
      {showDeleteModal && (
        <>
          <div className="fixed inset-0 z-40 transition-opacity duration-300" style={{ background: "rgba(253, 251, 247, 0.85)", backdropFilter: "blur(8px)" }} onClick={cancelDelete}></div>
          <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
            <div className="modal-enter w-full max-w-md bg-white rounded-2xl p-6 md:p-8 shadow-2xl border border-[#3E5645]/10">
              <h3 className="text-[#3E5645] text-xl font-bold mb-2">Delete Product?</h3>
              <p className="text-[#6B7A6E] text-sm mb-4">This action cannot be undone.</p>
              <div className="flex gap-3">
                <button onClick={cancelDelete} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium bg-[#F5F0EB] text-[#3E5645] border border-[#E8E0D8]">Cancel</button>
                <button onClick={confirmDelete} className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-[#C0392B]">Yes, Delete</button>
              </div>
            </div>
          </div>
        </>
      )}

      <AdminNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-[320px_1fr] gap-8">
        {/* ===== FORM ===== */}
        <div>
          <div className="rounded-2xl p-6 lg:sticky lg:top-24 bg-white border-2 border-dashed border-[#D4C8BC] shadow-sm">
            <h2 className="text-[#3E5645] text-lg mb-6" style={display}>{editingId ? "Update Product" : "Add a Product"}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Product Name" required className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border border-[#E8E0D8] outline-none" />
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" required rows={3} className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border border-[#E8E0D8] outline-none resize-none" />

              <div>
                <input type="number" value={originalPrice} onChange={(e) => setOriginalPrice(e.target.value)} placeholder="Original Price (MRP)" className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border border-[#A89B8C] outline-none" />
                <input type="number" value={sellingPrice} onChange={(e) => setSellingPrice(e.target.value)} placeholder="Selling Price (Actual)" required className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border-2 border-[#3E5645] outline-none font-semibold mt-2" />
              </div>

              {/* ===== CATEGORY: FULLY DYNAMIC ===== */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.12em] text-[#6B7A6E] font-semibold mb-1.5">Category (For Tabs)</label>
                <select
                  value={isNewCategory ? NEW_CATEGORY_VALUE : category}
                  onChange={handleCategorySelectChange}
                  className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border border-[#E8E0D8] outline-none"
                >
                  {existingCategories.length === 0 && (
                    <option value="" disabled>
                      No categories yet — add one below
                    </option>
                  )}
                  {existingCategories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                  <option value={NEW_CATEGORY_VALUE}>➕ Add New Category</option>
                </select>

                {isNewCategory && (
                  <input
                    value={newCategoryText}
                    onChange={(e) => setNewCategoryText(e.target.value)}
                    placeholder="Type new category name e.g. Puzzles"
                    autoFocus
                    className="w-full mt-2 px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border-2 border-[#3E5645] outline-none"
                  />
                )}
                <p className="text-[9px] text-[#A89B8C] mt-1">
                  Store page par is naam ka tab khud-ba-khud ban jayega.
                </p>
              </div>

              <select value={badge} onChange={(e) => setBadge(e.target.value)} className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border border-[#E8E0D8] outline-none">
                <option value="">No Badge</option>
                <option value="bestseller">🏆 Best Seller</option>
                <option value="new">✨ New</option>
                <option value="sale">🔥 Sale</option>
                <option value="limited">⚡ Limited</option>
              </select>

              <input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="Image URL 1 (Main)" className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border border-[#E8E0D8] outline-none" />
              <input value={imageUrl2} onChange={(e) => setImageUrl2(e.target.value)} placeholder="Image URL 2" className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border border-[#E8E0D8] outline-none" />
              <input value={imageUrl3} onChange={(e) => setImageUrl3(e.target.value)} placeholder="Image URL 3" className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border border-[#E8E0D8] outline-none" />
              <input value={imageUrl4} onChange={(e) => setImageUrl4(e.target.value)} placeholder="Image URL 4" className="w-full px-3.5 py-2.5 rounded-lg text-sm bg-[#FBF9F2] border border-[#E8E0D8] outline-none" />

              <button type="submit" disabled={saving} className="w-full py-3 rounded-xl font-semibold text-sm text-white bg-[#3E5645] hover:brightness-110 transition-all disabled:opacity-60">
                {saving ? "Saving…" : editingId ? "Update Product" : "Add to Store"}
              </button>
            </form>
          </div>
        </div>

        {/* ===== INVENTORY LIST ===== */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[#3E5645] text-lg" style={display}>Inventory ({products.length})</h2>

            {isDraggingOccurred && (
              <button
                onClick={handleSaveOrder}
                disabled={saving}
                className="px-4 py-2 rounded-lg font-semibold text-sm text-white bg-blue-600 hover:bg-blue-700 shadow-md transition-all disabled:opacity-60"
              >
                {saving ? "Saving..." : "💾 Save Order"}
              </button>
            )}
          </div>

          {loading ? <p className="text-[#6B7A6E] text-sm">Loading…</p> : products.length === 0 ? (
            <div className="rounded-2xl p-10 text-center border-2 border-dashed border-[#D4C8BC]"><p className="text-[#6B7A6E] text-sm">No products yet.</p></div>
          ) : (
            <div className="inventory-container space-y-2">
              {products.map((p, i) => {
                const productImages = getProductImages(p);
                return (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={() => handleDragStart(i)}
                    onDragEnter={() => handleDragEnter(i)}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    className={`product-card ${dragOverIndex === i ? "dragging-over" : ""}`}
                    style={{
                      borderColor: editingId === p.id ? "#3E5645" : "#E8E0D8",
                      background: editingId === p.id ? "rgba(62, 86, 69, 0.06)" : "#FFFFFF"
                    }}
                  >
                    <div className="drag-section">
                      <span className="drag-handle">⠿</span>
                      <span className="index-number">{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className="info-section">
                      <ProductImageSlider images={productImages} />
                      {productImages.length > 1 && <span className="multi-image-badge">📷</span>}
                      <div className="product-details">
                        <div className="product-name">{p.name}</div>
                        <div className="product-desc">{p.description}</div>
                        <div className="product-cat">📁 {p.category || "Uncategorized"}</div>
                        {p.badge && <span className="product-badge">🏷️ {p.badge}</span>}
                      </div>
                    </div>
                    <div className="actions-section">
                      <div className="price-info">
                        {p.originalPrice > 0 && p.originalPrice > p.sellingPrice && <span className="original-price">Rs. {p.originalPrice}</span>}
                        <span className="selling-price">Rs. {p.sellingPrice}</span>
                      </div>
                      <div className="action-buttons">
                        <button onClick={() => handleEditClick(p)} className="edit-btn">Edit</button>
                        <button onClick={() => openDeleteModal(p)} className="del-btn">Delete</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;